import { isPlainObject } from 'lodash'
import { getDefaultState, insertEmbedAsset, insertReferenceLink } from '../../../elements/Element/contentstack/image/Modal/ModalComponent'
import { insertEmbedEntry } from '../../../elements/Element/contentstack/reference/utils/referenceButton'
import { getEmbedLinkDetails } from '../../../elements/Element/contentstack/reference/utils/utils'
import { v4 } from 'uuid'
import { dispatch, listen } from './utils'
import { useEffect } from 'react'
import { exitRteFullScreenIfActive } from '../../../../../../utils/hooks/exitRteFullScreenIfActive'

export const NAVIGATE_MAIN_APP_ROUTE_EVENT = 'navigateMainAppRoute'
export const OPEN_EMBEDDED_ENTRY_IN_MODAL_EVENT = 'openEmbeddedEntryInModal'

function useRTEEmbedListeners({
    editor,
    editorRef,
    isPlugin,
    fieldMetadata,
    entryMetadata,
    isEntryInModal = false,
    isEntryInModalEnabled = false,
    isMainAppCreateEntryRoute = false,
}) {
    if (editorRef) {
        editor.editorRef = editorRef
    }
    const shouldGuardUnsavedBeforeLeave = isEntryInModal || isMainAppCreateEntryRoute
    const shouldDispatchMainAppRouteEvent =
        isEntryInModal || (isEntryInModalEnabled && isMainAppCreateEntryRoute)

    editor.isEntryInModal = isEntryInModal
    editor.isEntryInModalEnabled = isEntryInModalEnabled
    editor.pushRoute = ({ pathname, search = '', state }: { pathname: string; search?: string; state?: unknown }) => {
        if (shouldDispatchMainAppRouteEvent) {
            dispatch(NAVIGATE_MAIN_APP_ROUTE_EVENT, { pathname, search }, isPlugin)
            return
        }
        const { history } = editor.requestProps || {}
        if (!history) return
        const routeData: Record<string, unknown> = { pathname, search }
        if (state) routeData.state = state
        history.push(routeData)
    }

    editor.openEmbeddedEntryInModal = ({ entryUid, contentTypeUid, locale }: { entryUid: string; contentTypeUid: string; locale: string }) => {
        exitRteFullScreenIfActive(editor)
        dispatch(OPEN_EMBEDDED_ENTRY_IN_MODAL_EVENT, {
            entryUid,
            contentTypeUid,
            locale,
            parentEntryUid: entryMetadata?.entryUid,
            guardUnsavedBeforeOpen: shouldGuardUnsavedBeforeLeave,
        }, isPlugin)
    }

    useEffect(() => {

        if (!fieldMetadata?.absolutePath) return
        // This load event is different to that of load event in discussion hook, since the path is different
        dispatch(`${fieldMetadata?.absolutePath}-loaded`, {}, isPlugin)
    }, [!!window?.postRobot])

    const referenceTo = fieldMetadata?.reference_to ?? []
    if (editor.useV2EmbedPicker) {


        editor.showAssetEmbedModal = ({ type, prefilled }) => {
            try {
                const uuid = v4()
                const data = {
                    uuid,
                    showDisplayTypeSelector: type === 'asset',
                    showEmbedLinkSection: type === 'assetLink',
                    initialData: null,
                    isReplace: !!prefilled,
                    allowMultipleImageSelect: type === 'assetLink' ? false : true,
                    fieldMetadata: {
                        embedPath: fieldMetadata?.$$uid,
                        formPath: fieldMetadata?.$formPath,
                        $uid: fieldMetadata?.$uid,
                    }
                }
                const linkDetails = getDefaultState(editor)
                if (linkDetails?.linkTitle) {
                    data.initialData = {
                        linkTitle: linkDetails?.linkTitle,
                    }
                }

                if (prefilled) {
                    data.initialData = {
                        openInNewTab: linkDetails?.newTab,
                        linkTitle: linkDetails?.linkTitle,
                    }
                }
                if(linkDetails && linkDetails['title']){
                    if(!data.initialData) data.initialData = {}
                    data.initialData.title = linkDetails['title']
                }
                dispatch('showAssetEmbedModal', data, isPlugin)

                listen(`${uuid}-assetEmbed`, (data) => {

                    const { asset, openInNewTab: newTab, linkTitle, isInline, title } = data
                    if (!asset) return;
                    if (type === 'assetLink' && isPlainObject(asset)) {
                        insertReferenceLink({ editor, image: asset, linkTitle, newTab, title })
                    }
                    else if (type === 'asset') {
                        if (Array.isArray(asset)) insertEmbedAsset({ editor, asset, isInline })
                    }
                }, isPlugin, true)
            }
            catch (e) {
                console.error(e)
            }
        }


        editor.showEntryEmbedModal = ({ prefilled = false, type, attributes }) => {
            try {
                const uuid = v4()
                const data = {
                    uuid,
                    referenceTo,
                    showDisplayTypeSelector: type !== "link",
                    showEmbedLinkSection: type === 'link',
                    initialData: null,
                    isReplace: !!prefilled,
                    fieldMetadata: {
                        embedPath: fieldMetadata?.$$uid,
                        formPath: fieldMetadata?.$formPath,
                        $uid: fieldMetadata?.$uid,
                    }
                }
                const linkDetails = getEmbedLinkDetails(editor)

                if (linkDetails?.text) {
                    data.initialData = {
                        linkTitle: linkDetails?.text,
                    }
                }
                if (prefilled) {
                    data.initialData = {
                        entryUid: attributes?.['entry-uid'],
                        contentTypeUid: attributes?.['content-type-uid'],
                        locale: attributes?.['locale'],

                        isInline: attributes?.['display-type'] === "inline",
                        openInNewTab: linkDetails?.inNewTab,
                        linkTitle: linkDetails?.text,
                    }
                }
                if(linkDetails && linkDetails['title']){
                    if(!data.initialData) data.initialData = {}
                    data.initialData.title = linkDetails['title']
                }

                dispatch('showEntryEmbedModal', data, isPlugin)

                listen(`${uuid}-entryEmbed`, (data) => {
                    const { entry, openInNewTab, linkTitle, isInline, title } = data
                    if (!entry || !isPlainObject(entry)) return
                    const displayType = !type && isInline ? 'inline' : 'block'
                    const linkDetails = {
                        inNewTab: openInNewTab,
                        text: linkTitle,
                        href: entry.url,
                        title
                    }
                    const contentTypeUid = entry._content_type_uid
                    entry.contentTypeUid = contentTypeUid
                    insertEmbedEntry({
                        editor,
                        entry,
                        contentTypeUid,
                        displayType,
                        attributes,
                        linkDetails,
                        prefilled,
                        type
                    })
                }, isPlugin, true)

            }
            catch (e) {
                console.error(e)
            }

        }



    }

    if (editor.isDraft) {
        editor.getReferredObj = ({ ctUid, blockUid, pathname, search }) => {
            if (!entryMetadata || !ctUid || !blockUid || !pathname || !fieldMetadata) return {}
            const referredObjPayload = {
                entry_title: entryMetadata?.entryTitle ?? "",
                entry_uid: entryMetadata?.entryUid,
                variant_uid: entryMetadata?.variantUid,
                form_uid: entryMetadata?.contentTypeUid,
                locale: entryMetadata?.locale,
                location_to_push: {
                    pathname,
                    search
                },
                multiInstance: false,
                referred_field_uid: fieldMetadata.uid,
                referred_field_$uid: fieldMetadata.$uid,
                isOpenedFromRTE: true,
                type: entryMetadata?.entryUid ? "edit" : "create",
                _content_type_uid: ctUid,
                rteMetadata: {
                    blockUid,
                    absolutePath: fieldMetadata.absolutePath
                }
            }

            return { referredObjPayload }
        }

        editor.showCreateNewEntryModal = ({ prefilled = false, type, attributes }) => {
            try {
                exitRteFullScreenIfActive(editor)

                const uuid = v4().split('-').join('')
                const data = {
                    uuid,
                    showDisplayTypeSelector: type !== "link",
                    showEmbedLinkSection: type === 'link',
                    initialData: null,
                    isReplace: !!prefilled,
                    field: fieldMetadata
                }
                const linkDetails = getEmbedLinkDetails(editor)

                if (linkDetails?.text) {
                    data.initialData = {
                        linkTitle: linkDetails?.text,
                    }
                }
                if (prefilled) {
                    data.initialData = {
                        entryUid: attributes?.['entry-uid'],
                        contentTypeUid: attributes?.['content-type-uid'],
                        locale: attributes?.['locale'],

                        isInline: attributes?.['display-type'] === "inline",
                        openInNewTab: linkDetails?.inNewTab,
                        linkTitle: linkDetails?.text,
                    }
                }

                dispatch('showCreateNewEntryModal', data, isPlugin)

                listen(`${uuid}-entryEmbed`, (data) => {
                    const { entry, openInNewTab, linkTitle, isInline } = data
                    if (!entry || !isPlainObject(entry)) return
                    const displayType = !type && isInline ? 'inline' : 'block'
                    const linkDetails = {
                        inNewTab: openInNewTab,
                        text: linkTitle,
                        href: entry.url
                    }
                    const contentTypeUid = entry._content_type_uid
                    entry.contentTypeUid = contentTypeUid
                    insertEmbedEntry({
                        editor,
                        entry,
                        contentTypeUid,
                        displayType,
                        attributes,
                        linkDetails,
                        prefilled,
                        type,
                        uid: uuid
                    })
                }, isPlugin, true)

            }
            catch (e) {
                console.error(e)
            }

        }
    }


}


export default useRTEEmbedListeners
